import {
  addMarketplaceEntities,
  getMarketplaceEntities,
} from '../../../../ducks/marketplaceData.duck';
import { createImageVariantConfig } from '../../../../util/sdkLoader';
import { storableError } from '../../../../util/errors';

// ================ Action types ================ //

export const FETCH_RESALE_LISTINGS_REQUEST = 'app/ResaleSection/FETCH_RESALE_LISTINGS_REQUEST';
export const FETCH_RESALE_LISTINGS_SUCCESS = 'app/ResaleSection/FETCH_RESALE_LISTINGS_SUCCESS';
export const FETCH_RESALE_LISTINGS_ERROR = 'app/ResaleSection/FETCH_RESALE_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  listingRefs: [],
  fetchInProgress: false,
  fetchResolved: false,
  fetchError: null,
};

export default function resaleListingsReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_RESALE_LISTINGS_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchResolved: false,
        fetchError: null,
      };
    case FETCH_RESALE_LISTINGS_SUCCESS:
      return {
        ...state,
        listingRefs: payload.data.data.data
          .filter(l => l?.attributes?.publicData?._devSyncHidden !== true)
          .map(l => ({ id: l.id, type: l.type })),
        fetchInProgress: false,
        fetchResolved: true,
      };
    case FETCH_RESALE_LISTINGS_ERROR:
      return {
        ...state,
        fetchInProgress: false,
        fetchResolved: true,
        fetchError: payload,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchResaleListingsRequest = () => ({
  type: FETCH_RESALE_LISTINGS_REQUEST,
});

export const fetchResaleListingsSuccess = response => ({
  type: FETCH_RESALE_LISTINGS_SUCCESS,
  payload: { data: response },
});

export const fetchResaleListingsError = error => ({
  type: FETCH_RESALE_LISTINGS_ERROR,
  payload: error,
  error: true,
});

// ================ Thunks ================ //

export const fetchResaleListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchResaleListingsRequest());

  // Tests and some non-SDK render contexts don't provide an SDK instance.
  // In that case, treat this as an empty result instead of throwing.
  if (!sdk?.listings?.query) {
    const emptyResponse = { data: { data: [] } };
    dispatch(fetchResaleListingsSuccess(emptyResponse));
    return Promise.resolve(emptyResponse);
  }

  const params = {
    // Fetch resale listings - using the listing type for resale products
    pub_listingType: 'sell-used-products',
    // Only include in-stock items in resale cards
    minStock: 1,
    stockMode: 'match-undefined',
    // Sort by most recent (createdAt descending)
    sort: 'createdAt',
    // Fetch enough listings for up to ten home-page resale cards
    perPage: 10,
    // Include images and author info
    include: ['author', 'images'],
    // Request specific image fields with variants
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.listing-card',
      'variants.listing-card-2x',
    ],
    // Include image variants
    ...createImageVariantConfig('listing-card', 400, 1.34),
    ...createImageVariantConfig('listing-card-2x', 800, 1.34),
    // Limit to first image only
    'limit.images': 1,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchResaleListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchResaleListingsError(storableError(e)));
      throw e;
    });
};

// ================ Selectors ================ //

export const getResaleListings = state => {
  const { listingRefs } = state.ResaleSection;
  return getMarketplaceEntities(state, listingRefs);
};
